import { Injectable } from "@angular/core";
import '@babylonjs/inspector'

import {
  Scene,
  Engine,
  Color4,
  Color3,
  CubeTexture,
  ArcRotateCamera,
  Vector3,
  SceneLoader,
  PBRMaterial,
  Texture,
  StandardMaterial,
  MeshBuilder,
  ActionManager,
  ExecuteCodeAction,
  Animation,
  BezierCurveEase,
  Material,
  Tools,
  Mesh,
  NodeMaterial,
} from '@babylonjs/core';
import "@babylonjs/loaders";

import "@babylonjs/materials";


import { ButtonType } from "src/app/models/buttons/ButtonType";
import { MenuItemBasic } from "src/app/models/menus/MenuItemBasic";
import { ActionsEnum } from "src/app/models/ActionsEnum";
import { ButtonIcon } from "src/app/models/buttons/ButtonIcon";
import { ButtonColor } from "src/app/models/buttons/ButtonColor";
import { FloatingMenuItem } from 'src/app/models/menus/FloatingMenuItem';
import { MenuFactory } from "src/app/models/menus/MenuFactory";
import { env } from "process";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class SceneService {
  // private actualScene = SceneEnum.MATERIAL;
  // private currentScene;
  // public currentSceneObservable = new BehaviorSubject([]);

  private data;
  private engine;
  private canvas;
  private scene: Scene;
  private camera;
  private debugLayerEnabled;
  private client;
  private currentCameraTarget = "default";
  private envs = [];

  //Eventos disparados por botones transparentes que necesitan reflejarse en el menú
  private menuChangeSubject = new BehaviorSubject({});
  menuChangeObservable = this.menuChangeSubject.asObservable();

  constructor() { }

  public createScene(
    canvas: HTMLCanvasElement,
    engine: Engine,
    client: any,
    data: any,
    debugLayerEnabled: boolean
  ): Scene {
    this.data = data;
    this.engine = engine;
    this.canvas = canvas;
    this.client = client;
    this.debugLayerEnabled = debugLayerEnabled;
    this.scene = new Scene(engine);
    this.configurateScene(data);
    this.camera = this.createCamera(data, canvas);
    SceneLoader.ImportMesh(
      null,
      data.meshes[0].path,
      data.meshes[0].file,
      this.scene,
      (meshes: any) => {
        var boat = meshes[0].getChildren()[0];
        boat.setParent(null);
        meshes[0].dispose();
        boat.position.x = 5;

        this.scene.getAnimationGroupByName("backSunshadeAction").stop();
        this.scene.getAnimationGroupByName("terraceRootAction").stop();

        this.createMeshes(data);
        this.createTextures(this.scene.meshes, data, client);
        this.createTransparentButton(data.transparents_button, data.pov);
        this.changeObject("verado400");
        this.createAndSetFloatingAnimation(boat);
      }
    );
    return this.scene;
  }

  private createMeshes(data) {
    data.meshes/*.filter((mesh: any) => mesh.create)*/.forEach(meshConfig => {
      let mesh;
      if (meshConfig.create) {
        switch (meshConfig.type) {
          case "plane":
            mesh = MeshBuilder.CreatePlane(meshConfig.name, meshConfig.options, this.scene);
            break;
          case "sphere":
            mesh = MeshBuilder.CreateSphere(meshConfig.name, meshConfig.options, this.scene);
            break;
        }
      } else {
        mesh = this.scene.getMeshByName(meshConfig.name);
      }
      if (mesh) {
        Object.entries(meshConfig.properties).forEach(
          ([key, value]) => {
            if (value["x"] != null) {
              //Es una posición
              value = new Vector3(value["x"], value["y"], value["z"]);
            }
            mesh[key] = value;
          }
        );
      }
    });
  }

  private createTextures(meshes, data: any, client) {
    data.materials.forEach(async (matConfig) => {
      let mat: any;
      if (!matConfig.override) {
        mat = this.scene.getMaterialByName(matConfig.name);
      }
      /*
      //Si el material ya existe en la escena 
      if (mat) {
        //Actualizo sólo las properties
        Object.entries(matConfig.properties).forEach(([key, value]) => {
          if (value["b"] != null) {
            //Es un color
            value = new Color3(value["r"], value["g"], value["b"]);
          }
          if ((value instanceof String || typeof value === "string") && value.startsWith("#")) {
            value = Color3.FromHexString(String(value));
          }
          mat[key] = value;
        });
      } else {
        // Si no, lo creo
        if (matConfig.type === "PBR") {
          mat = new PBRMaterial(matConfig.name, this.scene);
        } else {
          if (matConfig.type === "NodeMaterial") {
            mat = await NodeMaterial.ParseFromFileAsync(matConfig.name, matConfig.maskedPBR, this.scene);
          } else {
            mat = new StandardMaterial(matConfig.name, this.scene);
          }
        }
        Object.entries(matConfig.properties).forEach(([key, value]) => {
          if (value["b"] != null) {
            //Es un color
            value = new Color3(value["r"], value["g"], value["b"]);
          }
          if ((value instanceof String || typeof value === "string") && value.startsWith("#")) {
            value = Color3.FromHexString(String(value));
          }
          mat[key] = value;
        });
        matConfig.textures.forEach((textureConfig) => {
          let text = new Texture(
            textureConfig.texture,
            this.scene
          );
          Object.entries(textureConfig.properties).forEach(([key, value]) => {
            text[key] = value;
          });
          if (textureConfig.block_name) {
            mat.getBlockByName(textureConfig.block_name).texture = text;
          } else {
            mat[textureConfig.name] = text;
          }
        });
      }
      */
      //Si el material ya existe en la escena 
      if (!mat) {
        // Si no, lo creo
        if (matConfig.type === "PBR") {
          mat = new PBRMaterial(matConfig.name, this.scene);
        } else {
          if (matConfig.type === "NodeMaterial") {
            mat = await NodeMaterial.ParseFromFileAsync(matConfig.name, matConfig.maskedPBR, this.scene);
          } else {
            mat = new StandardMaterial(matConfig.name, this.scene);
          }
        }
      }
      Object.entries(matConfig.properties).forEach(([key, value]) => {
        if (value["b"] != null) {
          //Es un color
          value = new Color3(value["r"], value["g"], value["b"]);
        }
        if ((value instanceof String || typeof value === "string") && value.startsWith("#")) {
          value = Color3.FromHexString(String(value));
        }
        mat[key] = value;
      });
      matConfig.textures.forEach((textureConfig) => {
        let text = new Texture(
          textureConfig.texture,
          this.scene
        );
        Object.entries(textureConfig.properties).forEach(([key, value]) => {
          text[key] = value;
        });
        if (textureConfig.block_name) {
          mat.getBlockByName(textureConfig.block_name).texture = text;
        } else {
          mat[textureConfig.name] = text;
        }
      });
      //TODO: Cambiar matConfig.target por foreach (va a ver targets)
      let mesh = meshes.filter((mesh) => matConfig.target === mesh.name)[0];

      if (mesh) {
        mesh.material = mat;
        if (mesh.name === "yacht") {
          mesh.parent = null;
        }
      }
    });

    // meshes.forEach(async (mesh) => {
    //   if (mesh.name === "yacht") {
    //     mesh.parent = null;
    //   }

    //   let matConfig = data.materials.filter((e) => e.target === mesh.name)[0];
    //   if (matConfig) {
    //     let mat: any = this.scene.getMaterialByName(matConfig.name);
    //     //Si el material ya existe en la escena 
    //     if (mat) {
    //         //Actualizo sólo las properties
    //         Object.entries(matConfig.properties).forEach(([key, value]) => {
    //           if (value["b"] != null) {
    //             //Es un color
    //             value = new Color3(value["r"], value["g"], value["b"]);
    //           }
    //           if ((value instanceof String || typeof value === "string") && value.startsWith("#")) {
    //             value = Color3.FromHexString(String(value));
    //           }
    //           mat[key] = value;
    //         });
    //     } else {
    //       // Si no, lo creo
    //       if (matConfig.type === "PBR") {
    //         mat = new PBRMaterial(matConfig.name, this.scene);
    //       } else {
    //         if (matConfig.type === "NodeMaterial") {
    //           mat = await NodeMaterial.ParseFromFileAsync(matConfig.name, `assets/materials/${client}/${matConfig.maskedPBR}`, this.scene);
    //         } else {
    //           mat = new StandardMaterial(matConfig.name, this.scene);
    //         }
    //       }
    //       Object.entries(matConfig.properties).forEach(([key, value]) => {
    //         if (value["b"] != null) {
    //           //Es un color
    //           value = new Color3(value["r"], value["g"], value["b"]);
    //         }
    //         if ((value instanceof String || typeof value === "string") && value.startsWith("#")) {
    //           value = Color3.FromHexString(String(value));
    //         }
    //         mat[key] = value;
    //       });
    //       matConfig.textures.forEach((textureConfig) => {
    //         let text = new Texture(
    //           `assets/materials/${client}/${textureConfig.texture}`,
    //           this.scene
    //         );
    //         Object.entries(textureConfig.properties).forEach(([key, value]) => {
    //           text[key] = value;
    //         });
    //         if (textureConfig.block_name) {
    //           mat.getBlockByName(textureConfig.block_name).texture = text;
    //         } else {
    //           mat[textureConfig.name] = text;
    //         }
    //       });
    //     }
    //     mesh.material = mat;
    //   }
    // });
  }

  private configurateScene(data: any) {
    this.scene.clearColor = new Color4(
      data.configuration.scene.clearColor.r,
      data.configuration.scene.clearColor.g,
      data.configuration.scene.clearColor.b,
      data.configuration.scene.clearColor.a
    );
    this.scene.ambientColor = new Color3(
      data.configuration.scene.ambientColor.r,
      data.configuration.scene.ambientColor.r,
      data.configuration.scene.ambientColor.r
    );

    if (data.configuration.scene.envTextures && data.configuration.scene.envTextures.length > 0) {
      data.configuration.scene.envTextures.forEach(envText => {
        let env = new CubeTexture(
          envText.url,
          this.scene
        );
        env.rotationY = envText.rotation;
        this.envs.push(env);
        if (envText.default) {
          this.scene.environmentTexture = env;
          this.scene.environmentIntensity = envText.intensity;
          if (envText.skybox) {
            let meshSkyBox = this.scene.createDefaultSkybox(this.scene.environmentTexture, false, 1000, 0, false);
            meshSkyBox.visibility = envText.skybox;
          }
        }
      });
    }
  }

  private toggleSkybox() {
    let nextEnv = this.envs.filter(envText => envText.name !== this.scene.environmentTexture.name)[0];
    let envConf = this.data.configuration.scene.envTextures.filter(envText => envText.url === nextEnv.name)[0];
    this.scene.environmentTexture = nextEnv;
    this.scene.environmentIntensity = envConf.intensity;
    let hdrSkyBox: any = this.scene.getNodeByName("hdrSkyBox");
    if (envConf) {
      if (envConf.skybox) {
        if (!hdrSkyBox) {
          hdrSkyBox = this.scene.createDefaultSkybox(this.scene.environmentTexture, false, 1000, 0, false);
        }
        hdrSkyBox.visibility = 1;
      } else {
        if (hdrSkyBox) {
          hdrSkyBox.visibility = 0;
        }
      }
      if (envConf.mesh_to_show && envConf.mesh_to_show.length > 0) {
        envConf.mesh_to_show.forEach(element => {
          this.scene.getMeshByName(element).visibility = 1;
        });
      }
      if (envConf.mesh_to_hide && envConf.mesh_to_hide.length > 0) {
        envConf.mesh_to_hide.forEach(element => {
          this.scene.getMeshByName(element).visibility = 0;
        });
      }
      let boat = this.scene.getTransformNodeByName("slx400OB_low");
      if (envConf.run_animation){
        this.scene.beginAnimation(boat, 0, 64, true);
      }else{
        this.scene.stopAnimation(boat);
        if (envConf.reset_position){
          boat.position = new Vector3(
            envConf.reset_position.x,
            envConf.reset_position.y,
            envConf.reset_position.z
          );
        }
        if (envConf.reset_rotation){
          boat.rotation = new Vector3(
            envConf.reset_rotation.x,
            envConf.reset_rotation.y,
            envConf.reset_rotation.z
          );
        }
        // boat.position = new Vector3(0,-0.5,0);
        // boat.rotation = new Vector3(1.5708,0,0);
      }
    }
  }

  private createCamera(data: any, canvas: HTMLCanvasElement) {
    let defaultCameraPosition = data.pov.filter((pov) => pov.default)[0];

    let cameraPosition = new Vector3(
      defaultCameraPosition.properties.position.alpha,
      defaultCameraPosition.properties.position.beta,
      defaultCameraPosition.properties.position.radius
    );
    //let cameraPosition = new Vector3(0, 0, 0);
    let camera = new ArcRotateCamera(
      "Camera",
      0,
      0,
      0,
      cameraPosition,
      this.scene
    );
    camera.attachControl(canvas, true);
    Object.entries(data.configuration.camera).forEach(
      ([key, value]) => (camera[key] = value)
    );

    Object.entries(defaultCameraPosition.properties).forEach(([key, value]) => {
      if (value && value["alpha"] != null) {
        //Es una posición
        value = new Vector3(value["alpha"], value["beta"], value["radius"]);
      }
      camera[key] = value;
    });

    return camera;
  }

  public getFloatingMenuItems() {
    return this.data.floating_menu.map((menuConfig) => { return new FloatingMenuItem(menuConfig) });
  }

  public getMenuItems() {
    let buttons = this.createMenu(this.data.menu);
    if (this.debugLayerEnabled) {
      buttons.push(
        new MenuItemBasic(
          "debug-layer",
          null,
          null,
          ActionsEnum.DEBUG_LAYER,
          new ButtonIcon(null, false, true, "fa fa-bug", false, true, false),
          [],
        )
      );
    }

    return buttons;
    // return this.data.menu;
  }

  private createMenu(items) {
    let buttons = [];
    items.forEach((menuConfig) => {
      let button = MenuFactory.createMenu(menuConfig);
      button.setMenu(this.createMenu(menuConfig.items));
      button.setButton(this.createButton(menuConfig.button));
      buttons.push(button);
    });

    return buttons;
  }

  private createButton(buttonConfig) {
    switch (buttonConfig.type) {
      case ButtonType.ICON:
        return new ButtonIcon(
          buttonConfig.friendlyName,
          Boolean(buttonConfig.default),
          buttonConfig.clickable,
          buttonConfig.icon,
          Boolean(buttonConfig.default),
          buttonConfig.brothers_can_unclick,
          buttonConfig.unclick_by_itself === false ? false : true,
          buttonConfig.alternative_icon
        );
      case ButtonType.COLOR:
        return new ButtonColor(
          buttonConfig.friendlyName,
          buttonConfig.color,
          Boolean(buttonConfig.default),
          buttonConfig.clickable,
          Boolean(buttonConfig.default),
          buttonConfig.brothers_can_unclick,
          buttonConfig.unclick_by_itself === false ? false : true
        );
    }
    return null;
  }

  private createTransparentButton(transparentsButton, pov) {
    transparentsButton.forEach((transparentsButtonConfig) => {
      let transparentButton = MeshBuilder.CreateBox(
        transparentsButtonConfig.name,
        transparentsButtonConfig.default_props,
        this.scene
      );
      if (transparentsButtonConfig.parentMesh) {
        this.scene
          .getMeshByName(transparentsButtonConfig.parentMesh)
          .addChild(transparentButton);
      }
      Object.entries(transparentsButtonConfig.properties).forEach(
        ([key, value]) => {
          if (value["x"] != null) {
            //Es una posición
            value = new Vector3(value["x"], value["y"], value["z"]);
          }
          transparentButton[key] = value;
        }
      );
      transparentButton.actionManager = new ActionManager(this.scene);
      transparentButton.actionManager.registerAction(
        new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
          switch (transparentsButtonConfig.type) {
            case ActionsEnum.CHANGE_POV:
              this.moveTo(pov, transparentsButtonConfig.target);
              break;
            case ActionsEnum.RUN_ANIMATIONS:
              this.runAnimationFor(transparentsButtonConfig.target);
              this.menuChangeSubject.next({
                "type": "animation",
                "target": transparentsButtonConfig.target,
                "status": this.scene.getAnimationGroupByName(transparentsButtonConfig.target)["status"]
              });
              break;
            case ActionsEnum.HIDE_MESH:
              this.toggleMesh(transparentsButtonConfig.target);
              this.menuChangeSubject.next({
                "type": "mesh",
                "target": transparentsButtonConfig.target,
                "status": this.scene.getMeshByName(transparentsButtonConfig.target).visibility
              });
              break;
          }
        })
      );
    });
  }

  private toggleMesh(meshName) {
    let mesh = this.scene
      .getMeshByName(meshName);
    mesh.visibility = mesh.visibility === 0 ? 1 : 0;
  }

  private moveTo(pov, target) {
    if (this.currentCameraTarget === target) {
      this.currentCameraTarget = "default";
    } else {
      this.currentCameraTarget = target;
    }
    let newPov = pov.filter((pov) => pov.name === this.currentCameraTarget)[0];
    let newTarget = new Vector3(
      newPov.properties.position.alpha,
      newPov.properties.position.beta,
      newPov.properties.position.radius
    );
    Object.entries(newPov.camera).forEach(
      ([key, value]) => (this.camera[key] = value)
    );
    this.scene.beginDirectAnimation(
      this.camera,
      this.createCameraAnimations(
        this.camera,
        newTarget,
        newPov.properties.radius,
        newPov.properties.beta,
        newPov.properties.lowerRadiusLimit,
        newPov.properties.upperRadiusLimit
      ),
      0,
      100,
      false
    );
  }

  doAction(item) {
    switch (item.type) {
      case ActionsEnum.CHANGE_TEXTURE:
        this.changeTexture(item.getMesh());
        break;
      case ActionsEnum.CHANGE_POV:
        this.moveTo(this.data.pov, item.getMesh());
        break;
      case ActionsEnum.RUN_ANIMATIONS:
        this.runAnimationFor(item.target, item.direction);
        break;
      case ActionsEnum.DEBUG_LAYER:
        let isEnabled = item.getButton()?.isClicked();
        isEnabled ? this.scene.debugLayer.show() : this.scene.debugLayer.hide();
        break;
      case ActionsEnum.CHANGE_ENVIRONMENT:
        this.toggleSkybox();
        break;
      case ActionsEnum.SCREENSHOT:
        this.takeScreenshot();
        break;
      case ActionsEnum.CHANGE_COLOR:
        this.changeColor(item);
        break;
      case ActionsEnum.HIDE_MESH:
        this.changeVisibility(item);
        break;
      case ActionsEnum.CHANGE_OBJECT:
        this.changeObject(item.mesh);
    }
  }

  private changeObject(item) {
    // item.meshes.forEach(element => {
    let mesh: any = this.scene.getMeshByName(item);
    if (mesh.parent) {
      mesh.parent._children.forEach((e: any) => {
        if (e.name === item) {
          e.visibility = 1;
          // e.isVisible = true;
        } else {
          e.visibility = false;
        }
      });
    } else {
      console.error("No tengo padre para decirle que oculte a mis hermanos");
    }
    //});
  }

  private changeVisibility(item) {
    item.meshes.forEach(element => {
      let mesh = this.scene.getMeshByName(element);
      switch (item.visibility) {
        case "VISIBLE":
          mesh.visibility = 1;
          break;
        case "HIDDEN":
          mesh.visibility = 0;
          break;
        default:
          mesh.visibility = mesh.visibility === 0 ? 1 : 0;
      }
    });
  }

  private changeColor(item) {
    item.colors.forEach((matConfig) => {
      item.targets.forEach(element => {
        let material: any = this.scene.getMaterialByName(element);
        if (matConfig.block_name) {
          let color: any = material.getBlockByName(matConfig.block_name);
          color.value = Color3.FromHexString(matConfig.color);
        } else {
          Object.entries(matConfig.properties).forEach(([key, value]) => {
            if (value["b"] != null) {
              //Es un color
              value = new Color3(value["r"], value["g"], value["b"]);
            }
            if ((value instanceof String || typeof value === "string") && value.startsWith("#")) {
              value = Color3.FromHexString(String(value));
            }
            material[key] = value;
          });
        }
      });
    });
  }

  private takeScreenshot() {
    Tools.CreateScreenshot(this.engine, this.scene.activeCamera, {precision: 1});
    // Tools.CreateScreenshotUsingRenderTarget(
    //   this.engine,
    //   this.scene.activeCamera,
    //   { width: this.canvas.width, height: this.canvas.height }
    // );
  }

  private changeTexture(meshes) {
    meshes.forEach((meshConfig) => {
      let mesh = this.scene.getMeshByName(meshConfig.name);
      mesh.material;
      meshConfig.textures.forEach((textureConfig) => {
        let text = new Texture(
          textureConfig.texture,
          this.scene
        );
        Object.entries(textureConfig.properties).forEach(([key, value]) => {
          text[key] = value;
        });
        const oldText = mesh.material[textureConfig.name];
        oldText.dispose();
        mesh.material[textureConfig.name] = text;
      });
    });
  }

  private runAnimationFor(item: any, direction?) {
    let animation = this.scene.getAnimationGroupByName(item);
    if (!animation.isPlaying) {
      if (!direction || direction === "BOTH") {
        if (!animation["status"] || animation["status"] === "start") {
          animation["status"] = "end";
          animation.start(false, 1, animation.from, animation.to, false);
        } else {
          animation["status"] = "start";
          animation.start(false, 1, animation.to, animation.from, false);
        }
      }
      if (direction === "FOWARD" && (!animation["status"] || animation["status"] === "start")) {
        animation["status"] = "end";
        animation.start(false, 1, animation.from, animation.to, false);
      }
      if (direction === "REVERSE" && animation["status"] === "end") {
        animation["status"] = "start";
        animation.start(false, 1, animation.to, animation.from, false);
      }
    }
  }

  //Camera Animations
  private createCameraAnimations(
    camera,
    newTarget,
    newRadius,
    newBeta,
    newMinRadius,
    newMaxRadius
  ) {
    let animations = [];

    // Camera Target Animation
    animations.push(
      this.createAnimation(
        "targetAnim",
        "target",
        80,
        Animation.ANIMATIONTYPE_VECTOR3,
        camera.target,
        newTarget
      )
    );

    // Camera Radius Animation
    animations.push(
      this.createAnimation(
        "radiusAnim",
        "radius",
        80,
        Animation.ANIMATIONTYPE_FLOAT,
        camera.radius,
        newRadius
      )
    );

    // Camera Alpha Animation
    /*animations.push(
                createAnimation(
                    "alphaAnim",
                    "alpha",
                    80,
                    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
                    camera.alpha,
                    newAlpha
                )
            );*/

    // Camera Beta Animation
    animations.push(
      this.createAnimation(
        "betaAnim",
        "beta",
        80,
        Animation.ANIMATIONTYPE_FLOAT,
        camera.beta,
        newBeta
      )
    );

    // Camera MinRadius Animation
    animations.push(
      this.createAnimation(
        "minRadiusAnim",
        "lowerRadiusLimit",
        80,
        Animation.ANIMATIONTYPE_FLOAT,
        camera.lowerRadiusLimit,
        newMinRadius
      )
    );

    // Camera MaxRadius Animation
    animations.push(
      this.createAnimation(
        "maxRadiusAnim",
        "upperRadiusLimit",
        80,
        Animation.ANIMATIONTYPE_FLOAT,
        camera.upperRadiusLimit,
        newMaxRadius
      )
    );

    // Camera LoBeta Animation
    /*animations.push(
                createAnimation(
                    "LoBetaLAnim",
                    "lowerBetaLimit",
                    80,
                    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
                    camera.lowerBetaLimit,
                    newLoBetaL
                )
            );
                
            // Camera UpBeta Animation
            animations.push(
                createAnimation(
                    "upBetaLAnim",
                    "upperBetaLimit",
                    80,
                    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
                    camera.upperBetaLimit,
                    newUpBetaL
                )
            );

                
            // Camera LoAlpha Animation
            animations.push(
                createAnimation(
                    "LoAlphaLAnim",
                    "lowerAlphaLimit",
                    80,
                    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
                    camera.lowerAlphaLimit,
                    newLoAlphaL
                )
            );
                
            // Camera UpAlpha Animation
            animations.push(
                createAnimation(
                    "upAlphaLAnim",
                    "upperAlphaLimit",
                    80,
                    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
                    camera.upperAlphaLimit,
                    newUpAlphaL
                )
            );*/

    return animations;
  }

  /**
   * @param nameAnimation
   * @param propertyName
   * @param fps
   * @param animationType
   * @param from
   * @param to
   */
  private createAnimation(
    nameAnimation: string,
    propertyName: string,
    fps: number,
    animationType: any,
    from,
    to
  ) {
    const animation = new Animation(
      nameAnimation,
      propertyName,
      fps,
      animationType,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    const keys = [
      {
        frame: 0,
        value: from,
      },
      {
        frame: 100,
        value: to,
      },
    ];

    animation.setKeys(keys);
    // Creating an easing function
    const easingFunction = new BezierCurveEase(0.5, 0, 0.5, 1);

    // Adding the easing function to the animation
    animation.setEasingFunction(easingFunction);
    return animation;
  }

  private createAndSetFloatingAnimation(boat) {
    //Floating Animation
    var fps = 15;
    //Create animations at 30 FPS
    var animationRotation = new Animation(
      "tutoAnimation",
      "rotation",
      fps,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );

    var animationPositionY = new Animation(
      "tutoAnimation",
      "position.y",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE
    );

    // Animation keys
    var keysRotation = [];
    var keysPositionY = [];

    var numberOfKeyframes = 64;
    var alpha = 0;

    for (var i = 0; i < numberOfKeyframes; i++) {
      // Rotation
      var x = Math.cos(alpha) / 150;
      var y = Math.sin(alpha) / 180;
      var z = 3.12 + Math.sin(alpha) / 150;
      //Position Y 
      keysRotation.push({
        frame: i,
        value: new Vector3(x, y, z)
      });
      keysPositionY.push({
        frame: i,
        value: (0 + Math.sin(alpha)) / 60
      });
      alpha += 0.1;
    }
    //Adding keys to the animation object
    animationRotation.setKeys(keysRotation);
    animationPositionY.setKeys(keysPositionY);
    boat.animations.push(animationRotation);
    boat.animations.push(animationPositionY);
  }

  /**Deprecated */
  // public createSceneOld() {
  //   this.currentScene = this.sceneMaterialService;
  //   var loadingScreen = new CustomLoadingScreen("I'm loading!!");
  //   // replace the default loading screen
  //   this.engineService.getEngine().loadingScreen = loadingScreen;
  //   // show the loading screen
  //   this.engineService.getEngine().displayLoadingUI();
  //   this.sceneImportedService
  //     .createScene(
  //       this.engineService.getEngine(),
  //       this.engineService.getCanvas()
  //     )
  //     .then((scene) => {
  //       this.currentScene = scene;
  //       this.setMenu();
  //       this.engineService.setScene(scene.getScene());
  //     });
  //   //this.currentSceneObservable.next(this.sceneMaterialService.getMenuItems());
  //   //return this.currentScene.getScene();
  // }

  // buttonClick(item: MenuItem) {
  //   if (
  //     item.getType().includes("CHANGE_SCENE") &&
  //     !item.getType().includes(this.currentScene.getType())
  //   ) {
  //     this.createNewScene(item.getType());
  //     return;
  //   }
  //   this.currentScene.change(item);
  // }

  // createNewScene(type: string) {
  //   if (type.includes(SceneEnum.VIDEO)) {
  //     this.currentScene = this.sceneVideoService;
  //     this.sceneVideoService.createScene(
  //       this.engineService.getEngine(),
  //       this.engineService.getCanvas()
  //     );
  //   }
  //   if (type.includes(SceneEnum.MATERIAL)) {
  //     this.currentScene = this.sceneMaterialService;
  //     this.sceneMaterialService.createScene(
  //       this.engineService.getEngine(),
  //       this.engineService.getCanvas()
  //     );
  //   }
  //   if (type.includes(SceneEnum.XYZ)) {
  //     this.currentScene = this.sceneXYZService;
  //     this.sceneXYZService.createScene(
  //       this.engineService.getEngine(),
  //       this.engineService.getCanvas()
  //     );
  //   }
  //   this.engineService.setScene(this.currentScene.getScene());
  //   this.currentSceneObservable.next(this.currentScene.getMenuItems());
  // }

  // public getVideoDome() {
  //   return this.sceneVideoService.getVideoDome();
  // }

  // public showChangeMaterial() {
  //   return this.actualScene === SceneEnum.MATERIAL;
  // }

  // public showChangeVideo() {
  //   return this.actualScene === SceneEnum.VIDEO;
  // }

  // public getMenuItems() {
  //   return this.currentScene.getMenuItems();
  // }

  // public setMenu() {
  //   this.currentSceneObservable.next(this.currentScene.getMenuItems());
  // }

  // public debugLayer() {
  //   this.currentScene.debugLayer();
  // }
}
